import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Divider, List, Popconfirm, Tooltip, Typography, message } from 'antd';
import apiClient from '../../../shared/services/apiClient';
import { useTranslation } from '../../../shared/i18n';
import { OAUTH_PROVIDERS, oauthErrorMessage } from '../../../shared/components/OAuthButtons';
import { OAUTH_BIND_NONCE_KEY, newOAuthNonce, takeOAuthNonce } from '../../../shared/services/authService';

const { Title, Text } = Typography;

/** Settings → Security: linked Google / GitHub sign-in. Hidden when nothing is configured or linked. */
const LoginMethods = () => {
    const { t } = useTranslation();
    const [state, setState] = useState(null); // { providers, identities, has_password }
    const [busy, setBusy] = useState(null);
    const hashHandled = useRef(false);

    const load = useCallback(async () => {
        try {
            const res = await apiClient.get('/api/user/oauth/identities', { skipGlobalErrorToast: true });
            setState(res.data?.data || null);
        } catch (_) {
            setState(null);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Back from the provider: #oauth_bind_code=<one-time code> or #oauth_error=<reason>.
    // The link is only made here, with this tab's nonce, as the logged-in user.
    useEffect(() => {
        if (hashHandled.current || !window.location.hash) return;
        const params = new URLSearchParams(window.location.hash.slice(1));
        const code = params.get('oauth_bind_code');
        const error = params.get('oauth_error');
        if (!code && !error) return;
        hashHandled.current = true;
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        if (error) {
            takeOAuthNonce(OAUTH_BIND_NONCE_KEY);
            message.error(oauthErrorMessage(error, t));
            return;
        }
        (async () => {
            try {
                await apiClient.post('/api/user/oauth/bind/complete',
                    { code, nonce: takeOAuthNonce(OAUTH_BIND_NONCE_KEY) },
                    { skipGlobalErrorToast: true });
                message.success(t('Linked'));
                load();
            } catch (e) {
                message.error(oauthErrorMessage(e.response?.data?.reason, t));
            }
        })();
    }, [t, load]);

    if (!state) return null;
    const identities = state.identities || [];
    const rows = OAUTH_PROVIDERS.filter((p) => state.providers?.[p.key] || identities.some((i) => i.provider === p.key));
    if (rows.length === 0) return null;

    const bind = async (provider) => {
        setBusy(provider);
        try {
            const res = await apiClient.post(`/api/user/oauth/${provider}/bind`, { nonce: newOAuthNonce(OAUTH_BIND_NONCE_KEY) });
            if (res.data?.data?.url) {
                window.location.href = res.data.data.url;
                return;
            }
        } catch (_) { /* global toast */ }
        setBusy(null);
    };

    const unbind = async (identity) => {
        setBusy(identity.provider);
        try {
            await apiClient.delete(`/api/user/oauth/identities/${identity.id}`, { skipGlobalErrorToast: true });
            message.success(t('Unlinked'));
            await load();
        } catch (e) {
            message.error(t(e.response?.data?.error || 'Operation failed'));
        } finally {
            setBusy(null);
        }
    };

    const isLast = !state.has_password && identities.length <= 1;

    return (
        <>
            <Divider />
            <Title level={4}>{t('Login methods')}</Title>
            <List
                dataSource={rows}
                renderItem={(p) => {
                    const identity = identities.find((i) => i.provider === p.key);
                    let action;
                    if (identity) {
                        action = isLast ? (
                            <Tooltip title={t('Set a password first (Set password above)')}>
                                <Button disabled>{t('Unlink')}</Button>
                            </Tooltip>
                        ) : (
                            <Popconfirm title={t('Unlink {{provider}}?', { provider: p.label })} onConfirm={() => unbind(identity)}>
                                <Button loading={busy === p.key}>{t('Unlink')}</Button>
                            </Popconfirm>
                        );
                    } else {
                        action = (
                            <Button type="primary" ghost loading={busy === p.key} disabled={!state.providers?.[p.key]} onClick={() => bind(p.key)}>
                                {t('Link')}
                            </Button>
                        );
                    }
                    return (
                        <List.Item actions={[action]}>
                            <List.Item.Meta
                                avatar={<span style={{ fontSize: 22 }}>{p.icon}</span>}
                                title={p.label}
                                description={identity
                                    ? <Text type="secondary">{identity.email || identity.name || t('Linked')}</Text>
                                    : <Text type="secondary">{t('Not linked')}</Text>}
                            />
                        </List.Item>
                    );
                }}
            />
        </>
    );
};

export default LoginMethods;
